import type { BrowserInfo, CurrencyRate, DepositMethod, PayoutMethod } from './types';

type MethodLike = Pick<DepositMethod, 'currencies'>;

export function methodSupportsCurrency(method: MethodLike, currency: string): boolean {
  return method.currencies.includes(currency);
}

export function methodsForCurrency<T extends MethodLike>(methods: readonly T[], currency: string): T[] {
  return methods.filter((m) => methodSupportsCurrency(m, currency));
}

/** `preferred` if supported, else the method's first currency, else null. */
export function firstSupportedCurrency(method: MethodLike, preferred?: string | null): string | null {
  if (preferred && method.currencies.includes(preferred)) return preferred;
  return method.currencies[0] ?? null;
}

export function needsBilling(method: Pick<DepositMethod | PayoutMethod, 'billing'>): boolean {
  return method.billing === 'required';
}

/**
 * Display-only conversion of EUR cents to `currency` cents using published rates.
 * The server decides the charged amount; never send this value as a EUR amount.
 */
export function convertFromEur(cents: number, currency: string, rates: readonly CurrencyRate[]): number | null {
  if (currency === 'EUR') return cents;
  const r = rates.find((x) => x.currency === currency);
  return r ? Math.round(cents * r.rate) : null;
}

export function formatMoney(cents: number, currency: string, locale?: string): string {
  try {
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency}`;
  }
}

/** Fee in cents for `amount` at `bps` basis points (rounded half up). */
export function feeCents(amount: number, bps: number): number {
  return Math.round((amount * bps) / 10_000);
}

export function collectBrowserInfo(): BrowserInfo {
  const info: BrowserInfo = {};
  if (typeof navigator !== 'undefined') {
    if (navigator.language) info.acceptLanguage = navigator.language;
    if (navigator.userAgent) info.userAgent = navigator.userAgent;
  }
  if (typeof screen !== 'undefined') {
    if (screen.width) info.screenWidth = screen.width;
    if (screen.height) info.screenHeight = screen.height;
  }
  try {
    info.timeZone = -new Date().getTimezoneOffset();
  } catch { /* ignore */ }
  return info;
}
